<template>

    <div>
        <section class="admin-form panel-form">
            <aside>
                <h2 class="font-medium text-lg">Information</h2>
                <p>
                    Create a new Campaign.
                </p>
            </aside>
            <div class="form-group">

                <div class="col-span-6 xl:col-span-4" :class="{ 'form-control-error': value.fieldHasErrors('name') }" v-if="value.isAdminUser()">
                    <label for="account">Account<span class="req-asterisk">*</span></label>
                    <div class="mt-2">
                        <select name="account" id="account" required aria-required="true" autocomplete="off" v-model="value.data.account">
                            <option :value="null">Select Account</option>
                            <option :value="account.slug" v-for="account in value.getAccounts()">{{ account.name }}</option>
                        </select>
                    </div>
                    <p class="input-error-message text-sm" v-if="value.fieldHasErrors('account')">
                        {{  value.getFieldError('account') }}
                    </p>
                </div>

                <div class="col-span-6 xl:col-span-4" :class="{ 'form-control-error': value.fieldHasErrors('name') }">
                    <label for="name">Name<span class="req-asterisk">*</span></label>
                    <div class="mt-2">
                        <input type="text" name="name" id="name" required aria-required="true" autocomplete="off" v-model="value.data.name">
                    </div>
                    <p class="input-error-message text-sm" v-if="value.fieldHasErrors('name')">
                        {{  value.getFieldError('name') }}
                    </p>
                </div>

                <div class="col-span-6 xl:col-span-4" :class="{ 'form-control-error': value.fieldHasErrors('currency') }">
                    <label for="currency">Currency<span class="req-asterisk" v-if="isNotEditing">*</span></label>
                    <div class="mt-2">
                        <select name="account" id="account" required aria-required="true" autocomplete="off" v-model="value.data.currency" v-if="isNotEditing">
                            <option :value="null">Select Currency</option>
                            <option :value="currency" v-for="currency in value.getCurrencies()">{{ currency }}</option>
                        </select>
                        <div class="font-medium" v-else>
                            {{ value.data.currency }}
                        </div>

                        <small>
                            The selected currency determines what prepaid products can be selected and what products are
                            available for purchase in this campaign.
                        </small>
                    </div>
                    <p class="input-error-message text-sm" v-if="value.fieldHasErrors('currency')">
                        {{  value.getFieldError('currency') }}
                    </p>
                </div>

                <div class="col-span-6 xl:col-span-4" :class="{ 'form-control-error': value.fieldHasErrors('active') }">
                    <div class="flex items-center">
                        <input id="active" name="active" type="checkbox" v-model="value.data.active">
                        <label for="active" class="ml-2 block text-sm text-slate-900">Active</label>
                    </div>

                    <p class="input-error-message text-sm" v-if="value.fieldHasErrors('active')">
                        {{  value.getFieldError('active') }}
                    </p>
                </div>

            </div>
        </section>

        <section class="admin-form panel-form">
            <aside>
                <h2 class="font-medium text-lg">BIN</h2>
                <p>
                    The hidden BIN range associated with this code (will automatically be prefixed when calling
                    Inspired Deck on the campaign frontend).
                </p>
            </aside>
            <div class="form-group">

                <div class="col-span-6 xl:col-span-4" :class="{ 'form-control-error': value.fieldHasErrors('hidden_bin') }" v-if="value.isAdminUser()">
                    <label for="account">Hidden BIN</label>
                    <div class="mt-2">
                        <input type="text" name="hidden_bin" id="hidden_bin" autocomplete="off" v-model="value.data.hidden_bin">

                        <small>
                            Only fill this in if the configuration requires it.
                        </small>
                    </div>
                    <p class="input-error-message text-sm" v-if="value.fieldHasErrors('hidden_bin')">
                        {{  value.getFieldError('hidden_bin') }}
                    </p>
                </div>

            </div>
        </section>

        <section class="admin-form panel-form">
            <aside>
                <h2 class="font-medium text-lg">Campaign Dates</h2>
                <p>
                    Select the length of time the campaign should be active.
                </p>
            </aside>
            <div class="form-group">

                <div class="col-span-6 xl:col-span-4" :class="{ 'form-control-error': value.fieldHasErrors('start_date') }">
                    <label for="start_date">Start Date</label>
                    <div class="mt-2">
                        <DateTimePicker id="start_date" autocomplete="off" v-bind:value="value.data.start_date" v-on:input="value.data.start_date = $event.target.value"></DateTimePicker>

                        <small>
                            Not selecting a start date will make the campaign start immediately.
                        </small>
                    </div>

                    <p class="input-error-message text-sm" v-if="value.fieldHasErrors('start_date')">
                        {{  value.getFieldError('start_date') }}
                    </p>
                </div>

                <div class="col-span-6 xl:col-span-4" :class="{ 'form-control-error': value.fieldHasErrors('end_date') }">
                    <label for="end_date">End Date</label>
                    <div class="mt-2">
                        <DateTimePicker id="end_date" autocomplete="off" v-bind:value="value.data.end_date" v-on:input="value.data.end_date = $event.target.value"></DateTimePicker>

                        <small>
                            Not selecting an end date will let this campaign run indefinitely.
                        </small>
                    </div>
                    <p class="input-error-message text-sm" v-if="value.fieldHasErrors('end_date')">
                        {{  value.getFieldError('end_date') }}
                    </p>
                </div>

            </div>
        </section>
    </div>

</template>

<script>
import DateTimePicker from "../../Support/DateTimePicker";
import {defineComponent} from "vue";
import {Formatter} from "../../../app/Support/Formatter";

export default defineComponent({
    props: {
        value: {
            required: true
        },
        edit: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    components: {DateTimePicker},
    data() {
        return {
            f: new Formatter,
        }
    },
    computed: {
        isEditing() {
            return this.edit === true;
        },
        isNotEditing() {
            return ! this.isEditing;
        }
    }
});
</script>
